/* eslint-disable */
import React, { Suspense } from "react";
import { supabase } from "../supabase";
import { ErrorBoundary } from "../ErrorBoundary";
import { CatalogView } from "./CatalogView";
import { useCanAccessPortal } from "../hooks/usePortalPermissions";
import PortalNoAccess from "./PortalNoAccess";
import { useLinkedItems } from "../hooks/useItemsByIds";
import profileStyles from "./MyProfile.module.css";
import { MailGate } from "../portals/mail/mailGate";

const QcPortal = React.lazy(() => import(/* webpackChunkName: "portal-qc" */ '../portals/qc/QcPortal').then(m => ({ default: m.QcPortal })));
const DevelopmentsTab = React.lazy(() => import(/* webpackChunkName: "portal-developments" */ '../DevelopmentsTab').then(m => ({ default: m.DevelopmentsTab })));
const OverviewTab = React.lazy(() => import(/* webpackChunkName: "tab-overview" */ './originals/overview/OverviewTab').then(m => ({ default: m.OverviewTab })));
const SalesPortal = React.lazy(() => import(/* webpackChunkName: "portal-sales" */ '../portals/sales/SalesPortal'));
const LensBankPage = React.lazy(() => import(/* webpackChunkName: "feature-lens-bank" */ '../portals/data/lensBank/LensBankPage').then(m => ({ default: m.LensBankPage })));
const AcetateBankPage = React.lazy(() => import(/* webpackChunkName: "feature-acetate-bank" */ '../portals/data/acetateBank/AcetateBankPage').then(m => ({ default: m.AcetateBankPage })));
const FilesBankPortal = React.lazy(() => import(/* webpackChunkName: "feature-files-bank" */ '../portals/files/FilesBankPortal').then(m => ({ default: m.FilesBankPortal })));
const UserManagement = React.lazy(() => import(/* webpackChunkName: "tab-users" */ '../UserManagement').then(m => ({ default: m.UserManagement })));
const AccessControl = React.lazy(() => import(/* webpackChunkName: "tab-access-control" */ '../portals/admin/accessControl/AccessControl'));
const MyAccess = React.lazy(() => import(/* webpackChunkName: "tab-my-access" */ '../portals/admin/accessControl/ordersTab/MyAccess'));
const DevRequestsPortal = React.lazy(() => import(/* webpackChunkName: "portal-devrequests" */ '../portals/devRequests/DevRequestsPortal').then(m => ({ default: m.DevRequestsPortal })));
const FavoritesTab = React.lazy(() => import(/* webpackChunkName: "tab-favorites" */ '../FavoritesTab').then(m => ({ default: m.FavoritesTab })));
const SettingsView = React.lazy(() => import(/* webpackChunkName: "tab-settings" */ './SettingsView').then(m => ({ default: m.SettingsView })));
const ProjectsPortal = React.lazy(() => import(/* webpackChunkName: "portal-projects" */ '../portals/projects/ProjectsPortal'));
const OrdersPortal = React.lazy(() => import(/* webpackChunkName: "portal-orders" */ '../portals/orders/OrdersPortal'));
// CLB-069 · the viewer ships INSIDE the Orders chunk (OrdersPortal.js re-exports it): its CSS modules are the Collection's, and a second
// chunk group importing them in another order fails `CI=true npm run build` (mini-css-extract "Conflicting order"). Code only — the data is server-side.
const CollectionViewerPortal = React.lazy(() => import(/* webpackChunkName: "portal-orders" */ '../portals/orders/OrdersPortal').then(m => ({ default: m.CollectionViewerPortal })));
// MAIL-1 — super admin only: MailGate renders it (and fetches this chunk) only for the gate's one person.
const MailPortal = React.lazy(() => import(/* webpackChunkName: "portal-mail" */ '../portals/mail/MailPortal'));
const TimelineScreen = React.lazy(() => import(/* webpackChunkName: "system-timeline" */ './system/TimelineScreen').then(m => ({ default: m.TimelineScreen })));

const SuspenseFallback = <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',fontFamily:'Montserrat',fontSize:'var(--text-2xs)',letterSpacing:3,color:'var(--text-label)',textTransform:'uppercase'}}>LOADING...</div>;

export function TabRouter({
  tab, user, role, myProfile, perm, isMobile, T, cardSt, inputSt, btnP, btnS, lblSt, br, br75,
  // Catalog
  items, pagedItems, totalCount, pageLoading, loaded, viewMode, catalogCols,
  selectedItems, setSelectedItems, setSelected, setHoverPreview, recentlyUploaded,
  toggleFavorite, isFav, hasMore, loadMoreItems, isCurrentlyFiltered, stats, debouncedSearch,
  clearAllFilters, fBrand, setFBrand, fGender, setFGender, fType, setFType, fMat, setFMat,
  fShape, setFShape, fStatus, setFStatus, fAssigned, setFAssigned, fCreatedBy, setFCreatedBy,
  fields, setFields, refreshAllItemsStats, allItemsStats, linksSlim, setItems, showToast,
  setShowExport, setShowImport,
  // Developments
  devs, setDevs, devSettings, setDevSettings, devView, setDevViewPersist,
  devSearch, devFilters, devSort, showDevForm, setShowDevForm,
  filteredDevsList, setFilteredDevsList, setFilteredIgnoringStatusList, favorites, setFavorites, userNameMap, activeDevs,
  // Dashboard
  devOverdueCount, devRequests, myDesignerNames, allDesignerNames = [], setTab,
  // Dev form prefill (from dev request accept flow)
  devFormPrefill, devFormRequestLink, onDevFormPrefillClear,
  // Settings
  theme, setTheme, setCatalogViewSize, catalogViewSize, showBrandManager, setShowBrandManager,
  fetchPagedItems, confirmDialog,
  // T-1015 — BASED ON bidirectional Map<item_id, dev[]> for grid eyebrow
  linkedDevsMap,
}) {
  // Access-engine route enforcement (deny-by-default; super-admin passes via
  // useCanAccessPortal). Step 3 gated projects; Step 4 closes the direct-URL hole on
  // the four live portals — same authority that already gates nav + hub, so no one
  // who can currently see a portal loses access; it only blocks URL/state navigation
  // to a portal the user has no row for (or role='none').
  const canProjects = useCanAccessPortal('projects');
  // app-shell 2026-09-14: the Developments portal's catalogItems are the items its developments are
  // based on, by id — never "every item" (and never the first catalog page as a stand-in).
  const linkedItems = useLinkedItems(devs);
  const canCatalog = useCanAccessPortal('catalog');
  const canDev = useCanAccessPortal('developments');
  const canQc = useCanAccessPortal('qc');
  const canSales = useCanAccessPortal('sales');
  const canLens = useCanAccessPortal('lens');   // Data portal (Lens Data Base) — default-deny
  const canAcetate = useCanAccessPortal('acetate'); // Data portal (Acetate Data Base) — default-deny
  const canFiles = useCanAccessPortal('files');     // Data portal (Files Data Base) — default-deny
  const canOrders = useCanAccessPortal('orders'); // Orders Phase 1 — default-deny, same authority
  const canCollection = useCanAccessPortal('collection'); // CLB-069 — the Collection-only role (the Wall, read-only)
  return (
    <>
      {/* ══ CATALOG (Originals) ══ Access-engine Step 4 — route-gated. */}
      {tab === "catalog" && (canCatalog ? (
        <ErrorBoundary><CatalogView items={items} pagedItems={pagedItems} totalCount={totalCount} pageLoading={pageLoading} loaded={loaded}
          viewMode={viewMode} isMobile={isMobile} catalogCols={catalogCols} selectedItems={selectedItems} setSelectedItems={setSelectedItems}
          setSelected={setSelected} setHoverPreview={setHoverPreview} recentlyUploaded={recentlyUploaded}
          toggleFavorite={toggleFavorite} isFav={isFav} hasMore={hasMore} loadMoreItems={loadMoreItems}
          isCurrentlyFiltered={isCurrentlyFiltered} stats={stats} debouncedSearch={debouncedSearch} clearAllFilters={clearAllFilters}
          fBrand={fBrand} setFBrand={setFBrand} fGender={fGender} setFGender={setFGender} fType={fType} setFType={setFType}
          fMat={fMat} setFMat={setFMat} fShape={fShape} setFShape={setFShape} fStatus={fStatus} setFStatus={setFStatus}
          fAssigned={fAssigned} setFAssigned={setFAssigned} fCreatedBy={fCreatedBy} setFCreatedBy={setFCreatedBy}
          fields={fields} perm={perm} refreshAllItemsStats={refreshAllItemsStats}
          showToast={showToast} T={T} cardSt={cardSt} inputSt={inputSt} supabase={supabase}
          linkedDevsMap={linkedDevsMap} /></ErrorBoundary>
      ) : <PortalNoAccess portal="Originals" />)}

      {/* ══ DEVELOPMENTS ══ Access-engine Step 4 — route-gated. Kept always-mounted
          (display toggle) for users WITH access to preserve state across tabs; users
          WITHOUT access never mount it and see the no-access panel on the dev tab. */}
      {canDev ? (
      <div style={{ display: tab === "developments" ? "block" : "none" }}>
        <ErrorBoundary><Suspense fallback={SuspenseFallback}><DevelopmentsTab
          isActive={tab === "developments"}
          devSettingsFromApp={devSettings} onDevSettingsChange={setDevSettings}
          user={user} role={role} myProfile={myProfile} catalogItems={linkedItems}
          catalogFields={fields}
          toggleFavorite={toggleFavorite} isFav={isFav}
          externalDevs={devs} onDevsChange={setDevs}
          favorites={favorites} setFavorites={setFavorites}
          T={T} cardSt={cardSt} inputSt={inputSt} btnP={btnP} btnS={btnS} lblSt={lblSt} br={br} br75={br75}
          showToast={showToast} confirm={confirmDialog} isMobile={isMobile} userNameMap={userNameMap}
          activeView={devView} onViewChange={setDevViewPersist}
          externalSearch={devSearch} externalFilters={devFilters} externalSort={devSort}
          showNewForm={showDevForm} onShowNewForm={setShowDevForm}
          onFilteredChange={setFilteredDevsList}
          onFilteredIgnoringStatusChange={setFilteredIgnoringStatusList}
          prefillDev={devFormPrefill} devFormRequestLink={devFormRequestLink}
          onPrefillClear={onDevFormPrefillClear}
          linkedDevsMap={linkedDevsMap}
        /></Suspense></ErrorBoundary>
      </div>
      ) : (tab === "developments" && <PortalNoAccess portal="Developments" />)}

      {/* ══ OVERVIEW (merged Dashboard + Analytics) ══ */}
      {tab === "overview" && (
        <Suspense fallback={SuspenseFallback}>
          <OverviewTab stats={stats} activeDevs={linksSlim} devs={linksSlim}
            items={items} allItemsStats={allItemsStats}
            T={T} cardSt={cardSt} isMobile={isMobile}
            role={role} myProfile={myProfile} setTab={setTab} />
        </Suspense>
      )}

      {/* ══ USERS → Access Control (consolidated) ══ */}
      {tab === "users" && role === "admin" && (
        <Suspense fallback={SuspenseFallback}>
          <AccessControl
            currentUser={user}
            showToast={showToast}
            confirm={confirmDialog}
            designers={allDesignerNames}
          />
        </Suspense>
      )}

      {/* ══ SALES PORTAL (new) ══ Access-engine Step 4 — route-gated. */}
      {tab === "sales_portal" && (
        canSales
          ? <ErrorBoundary><Suspense fallback={SuspenseFallback}>
              <SalesPortal user={user} myProfile={myProfile} showToast={showToast} />
            </Suspense></ErrorBoundary>
          : <PortalNoAccess portal="Sales" />
      )}

      {/* ══ ORDERS PORTAL ══ Phase 1 — route-gated on the same authority as nav + drawer. */}
      {tab === "orders" && (
        canOrders
          ? <ErrorBoundary><Suspense fallback={SuspenseFallback}>
              <OrdersPortal user={user} showToast={showToast} />
            </Suspense></ErrorBoundary>
          : <PortalNoAccess portal="Orders" />
      )}

      {/* ══ COLLECTION (viewer) ══ CLB-069 — route-gated on its own portal row. NOT OrdersPortal: that loads the
          whole Book; this is the Wall alone, on the whitelisted envelope the server gives this role. */}
      {tab === "collection" && (
        canCollection
          ? <ErrorBoundary><Suspense fallback={SuspenseFallback}><CollectionViewerPortal user={user} /></Suspense></ErrorBoundary>
          : <PortalNoAccess portal="Collection" />
      )}

      {/* ══ LENS DATA BASE (DATA department; reached only from the hamburger drawer) ══ */}
      {tab === "lensBank" && (canLens ? (
        <ErrorBoundary><Suspense fallback={SuspenseFallback}>
          <LensBankPage user={user} showToast={showToast} />
        </Suspense></ErrorBoundary>
      ) : <PortalNoAccess portal="Data" />)}

      {/* ══ ACETATE DATA BASE (DATA department; Hub pill + hamburger drawer, like the lens bank) ══ */}
      {tab === "acetateBank" && (canAcetate ? (
        <ErrorBoundary><Suspense fallback={SuspenseFallback}>
          <AcetateBankPage user={user} />
        </Suspense></ErrorBoundary>
      ) : <PortalNoAccess portal="Acetate" />)}

      {/* ══ FILES DATA BASE (DATA department; Hub pill, the third bank beside lens and acetate) ══ */}
      {tab === "filesBank" && (canFiles ? (
        <ErrorBoundary><Suspense fallback={SuspenseFallback}>
          <FilesBankPortal user={user} />
        </Suspense></ErrorBoundary>
      ) : <PortalNoAccess portal="Files" />)}

      {/* ══ FAVORITES ══ */}
      {tab === "favorites" && (
        <Suspense fallback={SuspenseFallback}><FavoritesTab T={T} cardSt={cardSt} isMobile={isMobile}
          favorites={favorites} setFavorites={setFavorites}
          isFav={isFav} toggleFavorite={toggleFavorite}
          setTab={setTab} setSelected={setSelected} /></Suspense>
      )}

      {/* ══ TIMELINE ══
          UNGATED by design. useCanAccessPortal is default-deny and there is no
          'timeline' portal id, so gating this the way the portals are gated
          would return false for every user, super-admins included. Row scoping
          is enforced server-side by get_unified_timeline (SECURITY INVOKER):
          a non-admin just receives fewer sources back. */}
      {tab === "timeline" && (
        <Suspense fallback={SuspenseFallback}><TimelineScreen /></Suspense>
      )}

      {/* ══ MY PROFILE ══ */}
      {tab === "my_profile" && (
        <>
          <div className={profileStyles.profileRoot} style={{ maxWidth:560 }}>
            <Suspense fallback={SuspenseFallback}><UserManagement supabase={supabase} showToast={showToast} confirm={confirmDialog} T={T} cardSt={cardSt}
              btnP={btnP} btnS={btnS} inputSt={inputSt} lblSt={lblSt} br={br} br75={br75}
              allDesigners={allDesignerNames}
              singleUser={user} /></Suspense>
          </div>
          {/* My access (OD2-P4, CLB-106): read-only, for anyone with Orders access; its own chunk, nothing new in the profile above */}
          {canOrders && (
            <div style={{ marginTop:24 }}>
              <Suspense fallback={SuspenseFallback}><MyAccess /></Suspense>
            </div>
          )}
        </>
      )}

      {/* ══ DEV REQUESTS ══ */}
      {tab === "dev_requests" && (
        <Suspense fallback={SuspenseFallback}><DevRequestsPortal
          user={user} role={role} myProfile={myProfile} myDesignerNames={myDesignerNames}
          showToast={showToast} setTab={setTab}
        /></Suspense>
      )}

      {/* ══ QC PORTAL ══ Access-engine Step 4 — route-gated. */}
      {tab === "qc" && (
        canQc
          ? <ErrorBoundary><Suspense fallback={SuspenseFallback}><QcPortal user={user} role={role} /></Suspense></ErrorBoundary>
          : <PortalNoAccess portal="QC" />
      )}

      {/* ══ SETTINGS ══ */}
      {tab === "settings" && perm.canSettings && (
        <Suspense fallback={SuspenseFallback}><SettingsView T={T} theme={theme} setTheme={setTheme} fields={fields} setFields={setFields} showToast={showToast}
          cardSt={cardSt} lblSt={lblSt} inputSt={inputSt} btnP={btnP} br={br} br75={br75}
          items={items} setItems={setItems} devs={devs} setDevs={setDevs}
          fetchPagedItems={fetchPagedItems} refreshAllItemsStats={refreshAllItemsStats}
          catalogViewSize={catalogViewSize} setCatalogViewSize={setCatalogViewSize}
          showBrandManager={showBrandManager} setShowBrandManager={setShowBrandManager} confirmDialog={confirmDialog} /></Suspense>
      )}

      {/* ══ PROJECTS ══ Access-engine Step 3 — gated by useCanAccessPortal
          (deny-by-default; super-admin passes). RLS still gates writes. */}
      {tab === "projects" && (
        canProjects
          ? <ErrorBoundary><Suspense fallback={SuspenseFallback}>
              <ProjectsPortal user={user} showToast={showToast} />
            </Suspense></ErrorBoundary>
          : <PortalNoAccess portal="Projects" />
      )}

      {/* ══ MAIL ══ MAIL-1 — super admin + mi_access only. Anyone else sees nothing and is sent Home (MailGate). */}
      {tab === "mail" && (
        <MailGate><ErrorBoundary><Suspense fallback={SuspenseFallback}><MailPortal /></Suspense></ErrorBoundary></MailGate>
      )}
    </>
  );
}
